import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import {
  CorteBlocoApi,
  CorteDevolucaoDados,
  CorteService,
} from "../../services/corte.service";

export type CorteDevolucaoAlvoEstorno = "barra" | "residuo";

export interface CorteDevolucaoAcao {
  tipo: "devolver" | "solicitar_autorizacao" | "estornar";
  quantidade: number;
  metros: number;
  /** Estorno: `barra` = `devolvido`; `residuo` = `devolvido_nao_elegivel`. */
  alvoEstorno?: CorteDevolucaoAlvoEstorno;
}

@Component({
  selector: "app-corte-devolucao-controle",
  templateUrl: "./corte-devolucao-controle.component.html",
  styleUrls: ["./corte-devolucao-controle.component.css"],
})
export class CorteDevolucaoControleComponent implements OnChanges {
  @Input() dados: CorteDevolucaoDados = {
    sobraTotal: 0,
    sobraLiquida: 0,
    sobraElegivel: 0,
    sobraNaoElegivel: 0,
    devolvido: 0,
    devolvidoNaoElegivel: 0,
    multiploElegivel: 0,
    multiploNaoElegivel: 0,
  };
  @Input() bloqueado = false;
  @Input() fatorConversao = 0;
  @Input() precoUnitario = 0;
  @Input() corteBloco: CorteBlocoApi | null = null;

  @Output() acao = new EventEmitter<CorteDevolucaoAcao>();

  qtdElegivel = 0;
  qtdNaoElegivel = 0;
  qtdEstorno = 0;
  estornoSelecionado: CorteDevolucaoAlvoEstorno | null = null;

  private ultimosDadosKey = "";

  constructor(private corte: CorteService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["dados"]) {
      const key = this.chaveDados(this.dados);
      if (key !== this.ultimosDadosKey) {
        this.ultimosDadosKey = key;
        this.qtdElegivel = 0;
        this.qtdNaoElegivel = 0;
        this.qtdEstorno = 0;
        this.estornoSelecionado = null;
      }
    }
    this.qtdElegivel = this.clampUnidades(this.qtdElegivel, this.maxQtdElegivel);
    this.qtdNaoElegivel = this.clampUnidades(this.qtdNaoElegivel, this.maxQtdNaoElegivel);
    this.reconciliarEstorno();
  }

  private chaveDados(d: CorteDevolucaoDados): string {
    return [
      d.sobraElegivel,
      d.sobraNaoElegivel,
      d.devolvido,
      d.devolvidoNaoElegivel,
      d.multiploElegivel,
      d.multiploNaoElegivel,
    ].join("|");
  }

  fmt(v: number): string {
    return this.corte.formatarMetros(v);
  }

  valorMetros(metros: number): number {
    return this.corte.calcularValorMetrosCorte(
      metros,
      this.fatorConversao,
      this.precoUnitario,
      this.corteBloco
    );
  }

  get valorBarraDisponivel(): number {
    return this.valorMetros(this.dados.sobraElegivel);
  }

  get valorResiduoDisponivel(): number {
    return this.valorMetros(this.dados.sobraNaoElegivel);
  }

  get valorBarraSelecionado(): number {
    return this.valorMetros(this.metrosElegivelSelecionados);
  }

  get valorResiduoSelecionado(): number {
    return this.valorMetros(this.metrosNaoElegivelSelecionados);
  }

  get exibirValorMonetario(): boolean {
    return (
      Number(this.fatorConversao) > 0 &&
      Number(this.precoUnitario) > 0
    );
  }

  get maxQtdElegivel(): number {
    return this.corte.maxUnidadesDevolucao(
      this.dados.sobraElegivel,
      this.dados.multiploElegivel
    );
  }

  get maxQtdNaoElegivel(): number {
    return this.corte.maxUnidadesDevolucao(
      this.dados.sobraNaoElegivel,
      this.dados.multiploNaoElegivel
    );
  }

  get metrosElegivelSelecionados(): number {
    return this.qtdElegivel * (this.dados.multiploElegivel || 0);
  }

  get metrosNaoElegivelSelecionados(): number {
    return this.qtdNaoElegivel * (this.dados.multiploNaoElegivel || 0);
  }

  get podeEstornarBarra(): boolean {
    return this.dados.devolvido > 0;
  }

  get podeEstornarResiduo(): boolean {
    return this.dados.devolvidoNaoElegivel > 0;
  }

  /** Total devolvido do tipo selecionado (barra ou resíduo). */
  get totalEstornoDisponivel(): number {
    if (this.estornoSelecionado === "barra") {
      return this.dados.devolvido;
    }
    if (this.estornoSelecionado === "residuo") {
      return this.dados.devolvidoNaoElegivel;
    }
    return 0;
  }

  /** Stepper habilitado quando há tipo selecionado e total devolvido > 0. */
  get estornoAtivo(): boolean {
    return this.estornoSelecionado != null && this.totalEstornoDisponivel > 0;
  }

  /** Múltiplo da barra: `sobra_elegivel` (barra) ou `sobra_nao_elegivel` (resíduo). */
  get multiploSobraEstornoAtual(): number {
    if (this.estornoSelecionado === "barra") {
      return this.dados.multiploElegivel;
    }
    if (this.estornoSelecionado === "residuo") {
      return this.dados.multiploNaoElegivel;
    }
    return 0;
  }

  get multiploEstornoAtual(): number {
    const m = this.corte.arredondar2(this.multiploSobraEstornoAtual);
    return m > 0 ? m : 0;
  }

  private maxUnidadesEstorno(totalDevolvido: number, multiploSobra: number): number {
    const total = this.corte.arredondar2(Number(totalDevolvido));
    if (total <= 0) {
      return 0;
    }
    const multiplo = this.corte.arredondar2(Number(multiploSobra));
    if (multiplo <= 0) {
      return 1;
    }
    const unidades = this.corte.maxUnidadesDevolucao(total, multiplo);
    return unidades > 0 ? unidades : 1;
  }

  get maxQtdEstornoBarra(): number {
    return this.maxUnidadesEstorno(
      this.dados.devolvido,
      this.dados.multiploElegivel
    );
  }

  get maxQtdEstornoResiduo(): number {
    return this.maxUnidadesEstorno(
      this.dados.devolvidoNaoElegivel,
      this.dados.multiploNaoElegivel
    );
  }

  get maxQtdEstornoAtual(): number {
    if (this.estornoSelecionado === "barra") {
      return this.maxQtdEstornoBarra;
    }
    if (this.estornoSelecionado === "residuo") {
      return this.maxQtdEstornoResiduo;
    }
    return 0;
  }

  get metrosEstornoSelecionados(): number {
    if (this.qtdEstorno <= 0) {
      return 0;
    }
    const total = this.totalEstornoDisponivel;
    const multiplo = this.multiploEstornoAtual;
    if (multiplo <= 0) {
      return this.corte.arredondar2(total);
    }
    return this.corte.arredondar2(
      Math.min(this.qtdEstorno * multiplo, total)
    );
  }

  get podeConfirmarEstorno(): boolean {
    return this.estornoAtivo && this.qtdEstorno > 0 && this.metrosEstornoSelecionados > 0;
  }

  selecionarTipoEstorno(tipo: CorteDevolucaoAlvoEstorno): void {
    if (this.bloqueado) {
      return;
    }
    const pode = tipo === "barra" ? this.podeEstornarBarra : this.podeEstornarResiduo;
    if (!pode) {
      return;
    }
    if (this.estornoSelecionado === tipo) {
      return;
    }
    this.estornoSelecionado = tipo;
    this.qtdEstorno = 0;
  }

  ajustarQtd(
    campo: "elegivel" | "naoElegivel" | "estorno",
    delta: number
  ): void {
    if (this.bloqueado) {
      return;
    }
    if (campo === "elegivel") {
      this.qtdElegivel = this.clampUnidades(
        this.qtdElegivel + delta,
        this.maxQtdElegivel
      );
    } else if (campo === "naoElegivel") {
      this.qtdNaoElegivel = this.clampUnidades(
        this.qtdNaoElegivel + delta,
        this.maxQtdNaoElegivel
      );
    } else if (this.estornoSelecionado) {
      this.qtdEstorno = this.clampUnidades(
        this.qtdEstorno + delta,
        this.maxQtdEstornoAtual
      );
    }
  }

  emitir(tipo: CorteDevolucaoAcao["tipo"]): void {
    if (this.bloqueado) {
      return;
    }
    if (tipo === "estornar") {
      const alvo = this.estornoSelecionado;
      const metros = this.metrosEstornoSelecionados;
      if (!alvo || !this.podeConfirmarEstorno) {
        return;
      }
      this.acao.emit({
        tipo: "estornar",
        quantidade: this.qtdEstorno,
        metros,
        alvoEstorno: alvo,
      });
      return;
    }
    if (tipo === "devolver") {
      const metros = this.metrosElegivelSelecionados;
      if (metros <= 0) {
        return;
      }
      this.acao.emit({
        tipo: "devolver",
        quantidade: this.qtdElegivel,
        metros,
      });
      return;
    }
    const metros = this.metrosNaoElegivelSelecionados;
    if (metros <= 0) {
      return;
    }
    this.acao.emit({
      tipo: "solicitar_autorizacao",
      quantidade: this.qtdNaoElegivel,
      metros,
    });
  }

  private reconciliarEstorno(): void {
    if (
      this.estornoSelecionado === "barra" &&
      !this.podeEstornarBarra
    ) {
      this.estornoSelecionado = null;
    } else if (
      this.estornoSelecionado === "residuo" &&
      !this.podeEstornarResiduo
    ) {
      this.estornoSelecionado = null;
    }
    this.qtdEstorno = this.clampUnidades(this.qtdEstorno, this.maxQtdEstornoAtual);
  }

  private clampUnidades(v: number, max: number): number {
    if (max <= 0) {
      return 0;
    }
    return Math.min(max, Math.max(0, Math.floor(Number(v) || 0)));
  }
}
